import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs, { type TabEntry } from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { ActorAccessSheet } from '../-components/ActorAccessSheet';
import { ActorsTable } from '../-components/ActorsTable';
import { NewRoleDialog } from '../-components/NewRoleDialog';
import { PermissionsHeader } from '../-components/PermissionsHeader';
import { ResourcesTable } from '../-components/ResourcesTable';
import { RolePermissionsSheet } from '../-components/RolePermissionsSheet';
import { RolesTable } from '../-components/RolesTable';
import { ServiceAccountsTab } from '../-components/ServiceAccountsTab';
import { TenantPickerCard } from '../-components/TenantPickerCard';
import { useCreateRole } from '../-hooks/useCreateRole';
import { usePermissionsPage } from '../-hooks/usePermissionsPage';
import {
  actorsQueryOptions,
  rolesQueryOptions,
} from '../-hooks/usePermissionsQueries';
import { useTenantPicker } from '../-hooks/useTenantPicker';
import { toActorRow } from '../-types';

const permissionsSearch = z.object({
  /** The company being administered. Everything on the page is scoped to it. */
  tenantId: z.string().optional(),
  /** Carried alongside so the header can name the tenant before a fetch lands. */
  tenantName: z.string().optional(),
  tab: z.string().optional(),
});

export const Route = createFileRoute('/management/permissions/(pages)/')({
  validateSearch: permissionsSearch,
  loaderDeps: ({ search }) => ({ tenantId: search.tenantId ?? '' }),
  // Nothing to prefetch until a tenant is chosen — every request carries its
  // header. Rejections are swallowed: a failed prefetch would otherwise hand
  // the route to the error boundary, and the page reports load failures itself.
  loader: ({ deps }) => {
    if (!deps.tenantId) return undefined;
    return Promise.all([
      queryClient.ensureQueryData(actorsQueryOptions(deps.tenantId)),
      queryClient.ensureQueryData(rolesQueryOptions(deps.tenantId)),
    ]).catch(() => undefined);
  },
  component: ManagementPermissionsPage,
});

function ManagementPermissionsPage() {
  const { getTranslation: t } = useTranslator({
    page: 'managementPermissions',
  });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const page = usePermissionsPage();
  const [isCreateRoleOpen, setCreateRoleOpen] = useState(false);
  const picker = useTenantPicker(page.tenant.tenantId === null);
  const createRole = useCreateRole(page.tenant.tenantId, role => {
    setCreateRoleOpen(false);
    page.roleEditor.open(role);
  });
  const { tab } = page;

  if (!page.tenant.tenantId) {
    return <TenantPickerCard picker={picker} onSelect={page.tenant.select} />;
  }

  const tabs: TabEntry[] = [
    { key: 'actors', label: t('tabs.actors') },
    {
      // A dropdown tab: the old page's Predefined / Custom sub-tabs are
      // children of Roles rather than a control inside the table, so which
      // half you are looking at is part of the URL.
      type: 'dropdown',
      key: 'roles',
      label: t('tabs.roles'),
      // Predefined and custom are two halves of one list, so the trigger says
      // which half is on screen — `Roles (Custom)`.
      showActiveChild: true,
      children: [
        { key: 'roles-predefined', label: t('roles.type.predefined') },
        { key: 'roles-custom', label: t('roles.type.custom') },
      ],
    },
    { key: 'resources', label: t('tabs.resources') },
    { key: 'serviceAccounts', label: t('tabs.serviceAccounts') },
  ];

  return (
    // Header and tabs sit outside `PageContainer`: the container is the
    // table's viewport-filling frame, and putting the chrome inside it made
    // the tab row scroll with the rows. Same split as the audit detail layout
    // — scrolling root, sticky tabs, and each tab claiming what is left.
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <div className='px-4 pt-3 pb-1 min-[1600px]:px-6 min-[1600px]:pt-4'>
        <PermissionsHeader
          tenantId={page.tenant.tenantId}
          tenantName={page.tenant.tenantName}
          onChangeCompany={page.tenant.clear}
        />
      </div>

      <div className='sticky top-0 z-20 bg-background'>
        <PageTabs
          tabs={tabs}
          mode='search'
          param='tab'
          defaultTab='actors'
          moreLabel={tShared('moreTabs')}
          className='pt-1 min-[1600px]:pt-2'
        />
      </div>

      <PageContainer>
        {tab.startsWith('roles') ? (
          <RolesTable
            rows={page.roles.rows}
            isLoading={page.roles.isLoading}
            onManage={page.roleEditor.open}
            // Creating belongs to Custom only: predefined roles are seeded per
            // company by migration, and the create route would produce a
            // custom one regardless of which tab it was pressed from.
            onCreate={
              page.roles.type === 'custom'
                ? () => setCreateRoleOpen(true)
                : undefined
            }
          />
        ) : tab === 'resources' ? (
          <ResourcesTable
            rows={page.resources.rows}
            catalog={page.resources.catalog}
            isLoading={page.resources.isLoading}
          />
        ) : tab === 'serviceAccounts' ? (
          <ServiceAccountsTab section={page.serviceAccounts} />
        ) : (
          <ActorsTable
            rows={page.actors.rows.map(toActorRow)}
            isLoading={page.actors.isLoading}
            onManage={page.actorAccess.openActor}
          />
        )}
      </PageContainer>

      <ActorAccessSheet access={page.actorAccess} />
      <RolePermissionsSheet editor={page.roleEditor} />
      <NewRoleDialog
        open={isCreateRoleOpen}
        onOpenChange={setCreateRoleOpen}
        form={createRole}
      />
    </div>
  );
}
